import { useMemo, useRef } from "react";
import { Sx, Text } from "@mantine/core";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_TableOptions,
  useMantineReactTable,
} from "mantine-react-table";
import { ICommonDataTableProps } from "./DataTable.types";
import React from "react";

export interface IBaseDataTableProps<TData extends Record<string, any>>
  extends ICommonDataTableProps<TData> {
  data: TData[] | null;
  isError?: boolean;
  isLoading?: boolean;
  isRefetching?: boolean;
  tableProps?: Partial<MRT_TableOptions<TData>>;
}

export const BaseDataTable = <TData extends Record<string, any>>({
  columns,
  sortable,
  filterable,
  resizable,
  hasPagination,
  showFooter,
  data,
  isError,
  isLoading,
  isRefetching,
  tableProps,
  columnResizeMode,
  mantineTableProps,
  mantineTableBodyRowProps: externalRowProps,
}: IBaseDataTableProps<TData>) => {
  //data and fetching state
  const mrtColumns = useMemo<MRT_ColumnDef<TData>[]>(
    () =>
      columns?.map(
        (c) =>
          ({
            accessorKey: c.fieldName,
            header: c.header,
            size: c.size,
            minSize: c.minSize,
            maxSize: c.maxSize,
            enableSorting:
              typeof c.sortable == "undefined" ? sortable : c.sortable,
            Cell: ({ cell }) =>
              c.cellTemplate ? (
                c.cellTemplate(cell.getValue(), cell.row.original)
              ) : (
                <Text>{cell.getValue()?.toString()}</Text>
              ),
            Footer: c.footer,
            enableColumnFilter: !!c.filterOptions,
            enableColumnActions: false,
            enableColumnFilterModes: false,
            filterFn: "equals",
            mantineFilterSelectProps: c.filterOptions
              ? { data: c.filterOptions as any }
              : undefined,
            filterVariant: c.filterOptions ? "select" : undefined,
          }) as MRT_ColumnDef<TData>,
      ) || [],
    [columns, sortable],
  );

  const { state, ...nonStateTableProps } = tableProps || {};

  const { sx, ...otherMantineTableProps } = mantineTableProps || {};

  const sxArray = sx
    ? Array.isArray(sx)
      ? sx.filter((i) => i).map((i) => i!)
      : [sx]
    : [];

  const table = useMantineReactTable({
    columns: mrtColumns,
    data: data || [],
    enableSorting: sortable,
    enablePagination: hasPagination,
    enableTableFooter: showFooter,
    enableFilters: filterable,
    enableTopToolbar: false,
    enableColumnDragging: false,
    enableColumnOrdering: false,
    enableColumnResizing: resizable,
    columnResizeMode: columnResizeMode,
    enableHiding: false,
    mantineTableProps: {
      sx: [
        (() => ({
          tableLayout: "fixed",
        })) as Sx,
      ].concat(sxArray),
      ...otherMantineTableProps,
    },
    mantineTableHeadCellProps: () => ({
      sx: () => ({
        whiteSpace: "normal",
        paddingRight: "0 !important",
        ".mantine-TableHeadCell-Content-Wrapper": {
          whiteSpace: "normal",
          overflow: "visible",
        },
        "button[aria-label='Unsorted']": {
          display: "none",
        },
      }),
    }),
    mantineTableHeadRowProps: () => ({
      sx: () => ({}),
    }),
    mantineTableBodyRowProps: ({ row }) => ({
      sx: (theme: any) => ({
        backgroundColor:
          row.index % 2 === 0
            ? theme.colorScheme === "dark"
              ? theme.colors.dark[6]
              : theme.colors.gray[0]
            : undefined,
      }),
      ...(typeof externalRowProps === "function"
        ? ((externalRowProps as any)?.({ row }) as any)
        : externalRowProps),
    }),
    mantineTableBodyCellProps: () => ({
      sx: () => ({
        whiteSpace: "normal",
        backgroundColor: "transparent",
      }),
    }),
    mantineToolbarAlertBannerProps:
      isError === true
        ? {
            color: "error",
            children: "Error loading data",
          }
        : undefined,
    state: {
      isLoading,
      columnVisibility: Object.fromEntries(
        columns?.map((c) => [c.fieldName, c.hidden === true ? false : true]) ||
          [],
      ),
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      density: "xs",
      ...state,
    },
    ...nonStateTableProps,
  });

  return <MantineReactTable table={table} />;
};
